'use client';

import Paragraph from '../common/Paragraph';
import ButtonLink from '../link/ButtonLink';
import TeaserPicture from '../../../../public/png/teaser.png';
import DirectusImage from '../image/DirectusImage';
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import CustomCursor from '../cursor/CustomCursor';

interface Props {}

export default function Teaser({}: Props) {
  const [offsetY, setOffsetY] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const linksRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const topOffset = ref.current ? ref.current.getBoundingClientRect().y : 0;
    if (topOffset > 0) {
      setOffsetY(topOffset);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const x = e.clientX;
    const y = e.clientY;
    const topOffset = ref.current ? ref.current.getBoundingClientRect().y : 0;
    setMousePosition({ x, y: y - topOffset });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative mb-[65px] bg-indigo-950 bg-teaser-bottom-right-ellipse-mobile lg:mb-[135px] lg:bg-teaser-bottom-right-ellipse">
      <div className="relative bg-teaser-forms-mobile md:bg-teaser-forms">
        <div className="relative h-auto w-full md:h-[660px]">
          <div className="h-full lg:bg-dark-bar">
            <div className="relative mx-auto flex px-4 pt-[161px] md:pl-48 lg:max-w-container lg:pt-[54px]">
              <div className="z-2 w-full md:w-2/3">
                <div className="mb-8 text-xs uppercase text-white opacity-60 md:mb-[60px]">
                  Fédération française de basketball
                </div>
                <span className="font-AgencyFBBlackComp text-[80px] uppercase leading-[72px] tracking-wide text-white antialiased lg:text-[180px] lg:leading-[162px]">
                  Devenez acteur de la FFBB
                </span>
              </div>
            </div>
            <div className="relative mx-auto flex w-full lg:max-w-container lg:pl-[32rem]">
              <div className="md:w-2/5" />
              <div className="z-2 flex w-full flex-col justify-center px-4 lg:w-3/5">
                <Paragraph styles="text-white leading-[34px]">
                  Le basket n&apos;est pas qu&apos;un jeu. C&apos;est aussi un sport, pour lequel on
                  vibre, une passion qui nous anime. Chacun à sa façon de la vivre dans son équipe
                  ou pour son club. Chacun a besoin de trouver sa place et de jouer son rôle pour
                  s&apos;épanouir.
                </Paragraph>
                <ButtonLink
                  name="S'impliquer dans le basket"
                  transparentBg={true}
                  styles="text-white p-6"
                  textSize="text-sm"
                  url={{ url: '/devenez-acteur-de-la-ffbb', external: false }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="relative flex w-full items-center justify-center overflow-hidden parallax-container md:h-[699px] md:bg-dark-bar">
          <div className="relative mx-auto w-full md:w-container">
            <div ref={ref} className="relative h-[400px] lg:h-[699px]">
              <DirectusImage
                src={TeaserPicture}
                alt={'Trouver des clubs, compétitions et lieux de pratiques'}
                dimension={{ sizes: '100vw' }}
                extraStyles={{
                  transform: `translateY(${offsetY * 0.5}px)`,
                  visibility: `${isHovering ? 'visible' : 'hidden'}`,
                  opacity: `${isHovering ? 1 : 0}`,
                  transition: 'opacity 0.3s ease, visibility 0.3s ease',
                }}
              />
              <div className="absolute z-10 h-full w-full px-4">
                <div className="relative mx-auto flex h-full flex-col justify-center md:mt-44 md:h-1/2 md:flex-row md:px-20 lg:pl-48">
                  <div className="lg:w-2/5"></div>
                  <div className="lg:w-3/5 lg:pl-60">
                    <div className="mb-5 text-xs uppercase text-white opacity-60">
                      {'Trouver des'}
                    </div>
                    <div
                      className="group relative"
                      ref={linksRef}
                      onMouseLeave={() => setIsHovering(false)}
                      onMouseEnter={() => setIsHovering(true)}
                      onMouseMove={handleMouseMove}
                    >
                      <div>
                        <span className="leading-12 font-AgencyFBBlackComp text-5xl uppercase tracking-wide text-white antialiased md:text-7xl md:leading-[76.50px]">
                          <Link
                            href="/trouver?type=clubs"
                            tabIndex={0}
                            target="_self"
                            className="teaser-link lg:cursor-none"
                          >
                            Clubs
                          </Link>
                        </span>
                      </div>
                      <div>
                        <span className="leading-12 font-AgencyFBBlackComp text-5xl uppercase tracking-wide text-white antialiased md:text-7xl md:leading-[76.50px]">
                          <Link
                            href="/trouver?type=competitions"
                            tabIndex={0}
                            target="_self"
                            className="teaser-link lg:cursor-none"
                          >
                            Compétitions
                          </Link>
                        </span>
                      </div>
                      <div>
                        <span className="leading-12 font-AgencyFBBlackComp text-5xl uppercase tracking-wide text-white antialiased md:text-7xl md:leading-[76.50px]">
                          <Link
                            href="/trouver?type=lieux_de_pratiques"
                            tabIndex={0}
                            target="_self"
                            className="teaser-link lg:cursor-none"
                          >
                            Lieux de pratiques
                          </Link>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {isHovering && (
                  <CustomCursor text="Trouver" mouseX={mousePosition.x} mouseY={mousePosition.y} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
